import type { GameState, SeatPlayer, PlayerAction, PlayerActionType } from '@/types';
import { calculatePreflopStrength, calculateHandStrength } from './hand-strength';

export class EasyAI {
  private randomFactor: number = 0.35; // 35% 概率做出"错误"决策

  decide(state: GameState, playerId: string): PlayerAction {
    const player = state.players.find(p => p.id === playerId);
    if (!player || !player.holeCards) {
      return { type: 'fold' };
    }

    // 随机犯错
    if (Math.random() < this.randomFactor) {
      return this.makeRandomDecision(state, player);
    }

    // 计算手牌强度
    const handStrength = state.communityCards.length === 0
      ? calculatePreflopStrength(player.holeCards)
      : calculateHandStrength(player.holeCards, state.communityCards);

    const validActions = this.getValidActions(state, player);

    // 简单决策逻辑
    if (handStrength > 0.8) {
      // 强牌：加注
      if (validActions.includes('raise')) {
        const raiseAmount = Math.min(
          state.currentBet + state.minRaise,
          player.chips,
          state.pots.reduce((sum, p) => sum + p.amount, 0) * 0.75
        );
        return { type: 'raise', amount: raiseAmount };
      }
      return { type: 'all-in' };
    } else if (handStrength > 0.5) {
      // 中等牌：跟注
      if (validActions.includes('call')) {
        return { type: 'call' };
      }
      if (validActions.includes('check')) {
        return { type: 'check' };
      }
      return { type: 'fold' };
    } else {
      // 弱牌：弃牌（但偶尔会跟注）
      if (handStrength > 0.3 && Math.random() < 0.3 && validActions.includes('call')) {
        return { type: 'call' };
      }
      if (validActions.includes('check')) {
        return { type: 'check' };
      }
      return { type: 'fold' };
    }
  }

  private makeRandomDecision(state: GameState, player: SeatPlayer): PlayerAction {
    const validActions = this.getValidActions(state, player);
    
    // 随机选择一个动作
    const randomAction = validActions[Math.floor(Math.random() * validActions.length)];
    
    switch (randomAction) {
      case 'raise':
        const randomRaise = Math.floor(Math.random() * player.chips * 0.3) + state.minRaise;
        return { type: 'raise', amount: Math.min(randomRaise, player.chips) };
      case 'all-in':
        return { type: 'all-in' };
      case 'call':
        return { type: 'call' };
      case 'check':
        return { type: 'check' };
      case 'fold':
      default:
        return { type: 'fold' };
    }
  }

  private getValidActions(state: GameState, player: SeatPlayer): PlayerActionType[] {
    const actions: PlayerActionType[] = ['fold'];
    const toCall = state.currentBet - player.currentBet;

    if (toCall === 0) {
      actions.push('check');
    }

    if (player.chips > toCall) {
      if (toCall > 0) actions.push('call');
      actions.push('raise');
    }

    if (player.chips > 0) {
      actions.push('all-in');
    }

    return actions;
  }
}
